import React from 'react';
import { withAppLayout } from "components/core/withAppLayout";
import BlockMapper from "blocks/BlockMapper";
import CtaBlock, { ICtaBlock } from "blocks/cta/CtaBlock";
import { GetStaticPropsContext } from "next";
import { createSubscription } from "components/hooks/usePreview";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import nextI18NextConfig from "../../../next-i18next.config";
import Container from "components/core/Container";
import Heading from "components/typography/Heading";
import Text from "components/typography/Text";
import useTrans from "components/hooks/useTrans";
import { getResourcesPage } from "queries/Resources";
import { IImage } from "types/base";
import { ICustomerCategory } from "pages/customers";
import { IFormPage } from "pages/forms/[slug]";
import Card from 'components/base/Card';

export async function getStaticProps(context: GetStaticPropsContext) {
    const query = {
        query: getResourcesPage(context.locale, context.locales),
        preview: context.preview,
        withAppData: true,
        locale: context.locale
    };

    const data = await createSubscription(context, query);

    return {
        props: {
            data: data,
            seo: data.initialData?.resourcesPage?.seo,
            appData: {
                ...data.initialData.header,
                ...data.initialData.footer,
            },
            ...(await serverSideTranslations(context.locale, ['common'], nextI18NextConfig)),
        }
    };
}

export type IResourcesPage = {
    id: string;
    content: any;
    cta: ICtaBlock;
};

export type IResourceItem = {
    id: string,
    title: string,
    form: IFormPage,
    featuredImage: IImage,
    intro: any,
    content: any,
    category: ICustomerCategory[],
    hideForThisLanguage: boolean
};

type Props = {
    resourcesPage: IResourcesPage;
    allResourceItems: IResourceItem[];
};

function Index(props: Props) {
    const { resourcesPage, allResourceItems } = props;
    const content = resourcesPage.content;
    const cta = resourcesPage.cta;
    const { t } = useTrans();

    const hasResourcesForThisLanguage = allResourceItems.filter(r => r.title).length;

    return (
        <>
            {/* Quick fix, we need to change this once we have spacing objects...*/}
            {content.map((block: any, index: number) => Object.keys(block).length > 0 && (
                <div key={'content-item-' + index}>
                    <div className={"h-28"} hidden={index === 0}>&nbsp;</div>
                    <BlockMapper key={block.id} {...block} />
                </div>
            ))}

            {hasResourcesForThisLanguage && (
                <section>
                    <Container>
                        <div className={"my-12 mt-44"}>
                            <Text color={"text-purple"} weight={"medium"}>
                                {t('allResources')}
                            </Text>
                            <Heading size={"lg"} weight={"semibold"}>{t('Become a loyalty expert')}</Heading>
                        </div>
                    </Container>
                </section>
            )}

            <section>
                <Container>
                    <div className={"grid grid-cols-1 gap-8 mt-4 lg:gap-6 lg:grid-cols-3 lg:gap-12 mb-10"}>
                        {allResourceItems.filter(r => r.title).map(resource => (
                            <Card
                                key={resource.id}
                                title={resource.title}
                                featuredImage={resource.featuredImage}
                                slug={`/forms/` + resource?.form?.slug}
                                intro={resource.intro}
                            />
                        ))}
                    </div>
                </Container>
            </section>

            {cta && (
                <section>
                    <CtaBlock {...cta} />
                </section>
            )}
        </>
    );
}

export default withAppLayout(Index);